import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Cpu, FileCheck, Shield, Smartphone, Sparkles, Wand2, Palette, Presentation, Receipt, Plane, Mic, Lightbulb, Glasses, Filter } from 'lucide-react';
import { FallbackUI } from './FallbackUI';

const equipmentIcons = [
  <Plane size={20} key="avata" />,
  <Plane size={20} key="neo" />,
  <Smartphone size={20} key="insta" />,
  <Smartphone size={20} key="iphone" />,
  <Mic size={20} key="mic" />,
  <Lightbulb size={20} key="light" />,
  <Glasses size={20} key="fpv" />,
  <Filter size={20} key="nd" />,
];

const contentIcons = [
  <Smartphone size={20} key="reels" />,
  <Smartphone size={20} key="tiktok" />,
  <Smartphone size={20} key="shorts" />,
  <Sparkles size={20} key="ai" />,
  <Wand2 size={20} key="staging" />,
  <Palette size={20} key="color" />,
  <Presentation size={20} key="beforeafter" />,
  <Presentation size={20} key="prop" />,
];

const legalIcons = [
  <Plane size={20} key="licensed" />,
  <Receipt size={20} key="invoice" />,
  <FileCheck size={20} key="legal" />,
  <Shield size={20} key="insured" />,
  <Shield size={20} key="guards" />,
  <Cpu size={20} key="workflow" />,
  <Shield size={20} key="safety" />,
];

export const TechTrustCards: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const equipment = t.techTrust.equipment;
  const content = t.techTrust.content;
  const legal = t.techTrust.legal;

  if (!equipment || !content || !legal) {
    return <FallbackUI message="Technology & Trust data loading..." />;
  }

  const sections = [
    { title: 'Equipment', items: equipment, icons: equipmentIcons, color: 'from-vilarias-accent/20 to-transparent' },
    { title: 'Content', items: content, icons: contentIcons, color: 'from-vilarias-glow/20 to-transparent' },
    { title: 'Trust', items: legal, icons: legalIcons, color: 'from-white/10 to-transparent' },
  ];

  return (
    <section id="tech" className="relative py-24 md:py-32 bg-vilarias-dark">
      <div className="absolute inset-0 bg-hero-glow opacity-20" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.techTrust.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {sections.map((section, sIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: sIndex * 0.15 }}
            >
              <div className="glass-card rounded-2xl p-6 h-full relative overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${section.color}`} />

                <h3 className="text-lg font-medium text-white mb-5 tracking-wide uppercase">
                  {section.title}
                </h3>

                <div className="space-y-3">
                  {section.items.map((item, index) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isVisible ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.3 + sIndex * 0.1 + index * 0.05 }}
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-vilarias-accent/10 flex items-center justify-center text-vilarias-accent group-hover:bg-vilarias-accent/20 transition-colors flex-shrink-0">
                        {section.icons[index] || <Cpu size={20} />}
                      </div>
                      <span className="text-sm text-vilarias-text-secondary group-hover:text-white transition-colors">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
